import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import Loader from '../../../common/components/Loader'
import {
  ShippingBand,
  ShippingBandPayload,
  ShippingStatus,
} from '../../../common/models/shipping-bands'
import FormGroup from '../../../core/components/FormGroup'
import { formatDate } from '../../../core/constants'
import {
  cancelShipping,
  getShippingById,
  shippingsStatus,
  updateShipping,
} from '../../../core/services/shippings'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'

export interface ViewShippingBandProps {
  visible: boolean
  setVisible: (show: boolean) => void
  onClose?: () => void
  setItems: (data: ShippingBand[]) => void
  item?: ShippingBand
  getData: () => void
}

interface IShippingValues {
  status: string
  packageReady: boolean
}

export const getDisplaValue = (name: string) => {
  switch (name) {
    case 'SENT':
      return 'Sent'
    case 'IN_PROCESS':
      return 'In Process'
    case 'WAITING':
      return 'Waiting'
    default:
      return ''
  }
}

export const ViewShippingBand = (props: ViewShippingBandProps): JSX.Element => {
  const { visible, setVisible, getData, item: itemProp } = props

  const [copied, setCopied] = useState<boolean>(false)
  const [item, setItem] = useState<ShippingBand | undefined>(undefined)

  const [statuses, setStatuses] = useState<ShippingStatus[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [showCancelOrderModal, setShowCancelOrderModal] =
    useState<boolean>(false)

  const handleClose = () => {
    setVisible(false)
    reset()
  }

  const getItem = useCallback(async () => {
    if (itemProp) {
      setLoading(true)
      try {
        const response = await getShippingById(itemProp.id)
        setItem(response.data)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
  }, [itemProp])

  useEffect(() => {
    if (visible) {
      getItem()
    }
  }, [getItem, visible])

  const getStatuses = useCallback(async () => {
    if (!item) return
    try {
      setLoading(true)
      const { data, status } = await shippingsStatus()

      if (status === 200) setStatuses(data)
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }, [item])

  useEffect(() => {
    if (item) {
      getStatuses()
      setTimeout(() => {
        setValue('status', item.status.name, {
          shouldValidate: true,
          shouldDirty: true,
        })
        setValue('packageReady', item.packageReady || false, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }, 100)
    }
    return () => {
      reset()
    }
  }, [item, getStatuses])

  const schema = yup.object().shape({
    status: yup.string().required(),
    packageReady: yup.boolean().required(),
  })

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    control,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<IShippingValues>({
    mode: 'onChange',
    resolver: yupResolver<IShippingValues>(schema),
    defaultValues: {
      packageReady: false,
    },
  })

  const packageReadyWatch = watch('packageReady')
  const statusWatch = watch('status')

  const submit = async (values: IShippingValues) => {
    try {
      const payload: ShippingBandPayload = {
        status: {
          name: values.status,
          displayValue: getDisplaValue(values.status),
        },
        packageReady: values.packageReady,
      }
      if (item) {
        const resp = await updateShipping(item?.id, payload)
        if (resp.status === 200) {
          setTimeout(() => {
            getData()
          }, 2000)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      handleClose()
    }
  }

  const handleReadyPackage = async (payload: ShippingBandPayload) => {
    if (item) {
      const resp = await updateShipping(item?.id, payload)
      if (resp.status === 200) {
        getData()
      }
    }
  }

  const handleCancelOrder = useCallback(async () => {
    if (item) {
      try {
        setLoading(true)
        const resp = await cancelShipping(item?.id)
        if (resp.status === 200) {
          getItem()
          getData()
        }
      } catch (error) {
        if (error instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(error.message)
        } else {
          console.error(error)
        }
      } finally {
        setLoading(false)
        setShowCancelOrderModal(false)
      }
    }
  }, [item, getData])

  return (
    <>
      {loading && <Loader />}
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Wristband Info" />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2 border-t border-monochrome-120 pt-4 mt-4">
            <div>
              {item?.status.name === 'WAITING' && (
                <Button
                  onClick={() => setShowCancelOrderModal(true)}
                  variant="secondary"
                  className="bg-accent-3 text-white hover:bg-accent-3/90"
                >
                  Cancel order
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                form="shipping-form"
                variant="primary"
                disabled={Object.keys(dirtyFields).length === 0}
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="shipping-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7 text-monochrome-1"
        >
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="font-bold mb-0">ID</p>
              <span>{item?.id}</span>
            </div>
            <div>
              <p className="font-bold mb-0">Buyer</p>
              <p className="mb-0">{item?.buyer.name}</p>
              <p className="mb-0">{item?.buyer.phone}</p>
              <p className="mb-0">{item?.buyer.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="font-bold mb-0">Ticket Name</p>
              <span>{item?.ticketName}</span>
            </div>
            <div>
              <p className="font-bold mb-0">Shipping Address</p>
              <p className="mb-0">{item?.shippingAddress.address}</p>
              <p className="mb-0">
                {item?.shippingAddress.country},{item?.shippingAddress?.city}
              </p>
              <p className="mb-0">{item?.shippingAddress.postCode}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex flex-col justify-between">
              <div>
                <p className="font-bold mb-0">Order Date</p>
                <span>{formatDate(item?.lastUpdate ?? 0)}</span>
              </div>

              <div className="mt-4">
                <FormGroup
                  labelClassName="text-nowrap"
                  label="Package Ready"
                  direction="row"
                  labelProps={{ htmlFor: 'packageReady' }}
                >
                  <Controller
                    control={control}
                    name="packageReady"
                    render={({ field }) => (
                      <Checkbox
                        id="packageReady"
                        checked={field.value}
                        disabled={item?.packageReady}
                        onCheckedChange={(checked) => {
                          field.onChange(checked)
                          if (checked) {
                            const payload: ShippingBandPayload = {
                              status: {
                                name: statusWatch,
                                displayValue: getDisplaValue(statusWatch),
                              },
                              packageReady: true,
                            }
                            handleReadyPackage(payload)
                          }
                        }}
                        className="ml-5"
                      />
                    )}
                  />
                </FormGroup>
              </div>
            </div>

            <div className="space-y-7">
              <div>
                <p className="font-bold mb-2">Status</p>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <SelectField
                      outline
                      value={field.value}
                      onValueChange={field.onChange}
                      error={errors?.status?.message}
                      options={statuses.map((status) => ({
                        label: status.displayValue,
                        value: status.name,
                      }))}
                    />
                  )}
                />
              </div>

              <div>
                <p className="font-bold mb-2">AWB Code</p>
                <div
                  className={`grid ${
                    item?.awbCode ? 'grid-cols-2' : 'grid-cols-1'
                  } gap-2`}
                >
                  <Input
                    value={item?.awbCode || ''}
                    disabled
                    type="text"
                    placeholder="AWB Code"
                  />
                  {!!item?.awbCode && (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(item?.awbCode ?? '')
                        setCopied(true)
                        setTimeout(() => {
                          setCopied(false)
                        }, 1000)
                      }}
                    >
                      {copied ? 'Copied' : 'Copy Code'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showCancelOrderModal}
        setDeleteConfirmationModal={setShowCancelOrderModal}
        cancelText
        deleteFunc={() => {
          handleCancelOrder()
        }}
        deleteTitle={'order'}
      />
    </>
  )
}
